import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    House,
    Airplane,
    MapPin,
    Clock,
    ArrowLeft,
    CheckCircle,
    XCircle,
    HourglassMedium,
    Trophy,
} from "@phosphor-icons/react";

function formatItalianDate(iso) {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("it-IT", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

export default function MatchDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const { refresh: refreshNotifs } = useNotifications();
    const [match, setMatch] = useState(null);
    const [players, setPlayers] = useState([]);
    const [convs, setConvs] = useState([]);
    const [checked, setChecked] = useState({});
    const [result, setResult] = useState({ home: "", away: "" });

    const load = useCallback(async () => {
        try {
            const [m, c, p] = await Promise.all([
                api.get(`/matches/${id}`),
                api.get(`/matches/${id}/convocations`),
                api.get("/players"),
            ]);
            setMatch(m.data);
            setConvs(c.data);
            setPlayers(p.data);
            if (m.data.is_finished) {
                setResult({
                    home: String(m.data.home_score ?? ""),
                    away: String(m.data.away_score ?? ""),
                });
            }
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("match load failed:", e);
            }
        }
    }, [id]);

    const saveResult = async (e) => {
        e.preventDefault();
        const home = parseInt(result.home);
        const away = parseInt(result.away);
        if (Number.isNaN(home) || Number.isNaN(away) || home < 0 || away < 0) {
            toast.error("Inserisci punteggi validi");
            return;
        }
        try {
            await api.put(`/matches/${id}/result`, {
                home_score: home,
                away_score: away,
            });
            toast.success("Risultato salvato");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => {
        load();
    }, [load]);

    if (!match) {
        return <div className="text-muted-foreground text-sm">Caricamento…</div>;
    }

    const isPlayer = user?.role === "player";
    const myPlayer = players.find((x) => x.user_id === user?.id);
    const myConv = myPlayer ? convs.find((c) => c.player_id === myPlayer.id) : null;

    const respond = async (status) => {
        try {
            await api.put(`/matches/${id}/convocations/confirm`, { status });
            toast.success(status === "confirmed" ? "Presenza confermata" : "Assenza registrata");
            load();
            refreshNotifs();
        } catch {
            toast.error("Errore nell'aggiornamento");
        }
    };

    const respondForKid = async (playerId, status) => {
        try {
            await api.put(`/matches/${id}/convocations/confirm/${playerId}`, { status });
            toast.success("Risposta aggiornata");
            load();
            refreshNotifs();
        } catch {
            toast.error("Errore");
        }
    };

    return (
        <div className="space-y-6" data-testid="match-detail">
            <Link
                to="/partite"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground press"
            >
                <ArrowLeft size={14} weight="bold" /> Torna alle partite
            </Link>

            <div className="rounded-2xl hairline bg-card overflow-hidden">
                <div className="p-6 relative">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full bg-primary/15 text-primary flex items-center gap-1">
                            {match.home ? (
                                <House size={12} weight="fill" />
                            ) : (
                                <Airplane size={12} weight="fill" />
                            )}
                            {match.home ? "Casa" : "Trasferta"}
                        </span>
                        <span className="num text-sm font-bold">{match.kickoff_time}</span>
                    </div>
                    <div className="num text-4xl md:text-6xl font-black uppercase leading-none mt-3">
                        {match.kickoff_time}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 capitalize">
                        {formatItalianDate(match.date)}
                    </div>
                    <div className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-4">
                        vs <span className="text-primary">{match.opponent}</span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                            <MapPin size={16} weight="duotone" />
                            {match.field_name || "Campo da definire"}
                        </span>
                        {match.meeting_time && (
                            <span className="flex items-center gap-1.5">
                                <Clock size={16} weight="duotone" />
                                Ritrovo {match.meeting_time}
                            </span>
                        )}
                    </div>
                    {match.notes && (
                        <p className="mt-4 text-sm text-foreground/90 whitespace-pre-line">
                            {match.notes}
                        </p>
                    )}
                </div>

                {/* Risultato / storico */}
                {match.is_finished && (
                    <div
                        data-testid="match-result-display"
                        className="px-6 py-5 border-t border-border/60 bg-primary/5 flex items-center gap-4"
                    >
                        <Trophy size={20} weight="fill" className="text-primary" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                Risultato finale
                            </div>
                            <div className="num text-3xl md:text-4xl font-black">
                                {match.home ? match.home_score : match.away_score}
                                <span className="text-muted-foreground mx-2">-</span>
                                {match.home ? match.away_score : match.home_score}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {match.home ? "Noi" : match.opponent}{" "}
                                <span className="mx-1">·</span>
                                {match.home ? match.opponent : "Noi"}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Inserisci/aggiorna risultato (admin/coach) */}
            {(user?.role === "admin" || user?.role === "coach") && (
                <form
                    onSubmit={saveResult}
                    className="rounded-2xl hairline bg-card p-5 space-y-3"
                    data-testid="match-result-form"
                >
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        {match.is_finished ? "Aggiorna risultato" : "Inserisci risultato"}
                    </div>
                    <div className="flex items-end gap-3">
                        <div className="flex-1">
                            <div className="text-xs text-muted-foreground mb-1">
                                {match.home ? "Noi (casa)" : match.opponent + " (casa)"}
                            </div>
                            <Input
                                data-testid="result-home-input"
                                type="number"
                                min="0"
                                value={result.home}
                                onChange={(e) =>
                                    setResult({ ...result, home: e.target.value })
                                }
                                required
                            />
                        </div>
                        <div className="num text-3xl font-black text-muted-foreground pb-1">
                            -
                        </div>
                        <div className="flex-1">
                            <div className="text-xs text-muted-foreground mb-1">
                                {match.home ? match.opponent + " (trasferta)" : "Noi (trasferta)"}
                            </div>
                            <Input
                                data-testid="result-away-input"
                                type="number"
                                min="0"
                                value={result.away}
                                onChange={(e) =>
                                    setResult({ ...result, away: e.target.value })
                                }
                                required
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="save-result-btn"
                            className="press h-10"
                        >
                            Salva
                        </Button>
                    </div>
                </form>
            )}

            {/* Player RSVP */}
            {isPlayer && myConv && (
                <div className="rounded-2xl hairline bg-card p-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Convocazione
                    </div>
                    <div className="font-display text-xl font-bold mt-1">Sei convocato</div>
                    <div className="flex gap-2 mt-4">
                        <Button
                            data-testid="rsvp-confirm-button"
                            onClick={() => respond("confirmed")}
                            className={`flex-1 h-12 text-xs uppercase tracking-widest press ${
                                myConv.status === "confirmed"
                                    ? "bg-primary text-primary-foreground"
                                    : ""
                            }`}
                        >
                            <CheckCircle size={16} weight="fill" className="mr-1" /> Ci sarò
                        </Button>
                        <Button
                            data-testid="rsvp-decline-button"
                            variant="outline"
                            onClick={() => respond("declined")}
                            className={`flex-1 h-12 text-xs uppercase tracking-widest press ${
                                myConv.status === "declined"
                                    ? "border-destructive text-destructive"
                                    : ""
                            }`}
                        >
                            <XCircle size={16} weight="fill" className="mr-1" /> Non ci sarò
                        </Button>
                    </div>
                </div>
            )}

            {/* Parent RSVP for kids */}
            {user?.role === "parent" &&
                players.length > 0 && (
                    <div className="rounded-2xl hairline bg-card p-5 space-y-3">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                            Convocazioni figli
                        </div>
                        {players.map((p) => {
                            const c = convs.find((x) => x.player_id === p.id);
                            return (
                                <div
                                    key={p.id}
                                    className="rounded-xl hairline p-3 flex items-center gap-3"
                                >
                                    <div className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-bold">
                                        {p.first_name[0]}
                                        {p.last_name[0]}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold">
                                            {p.first_name} {p.last_name}
                                        </div>
                                        <StatusPill status={c?.status} />
                                    </div>
                                    {c && (
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => respondForKid(p.id, "confirmed")}
                                                className="p-2 rounded-lg hover:bg-muted press"
                                                aria-label="Conferma"
                                            >
                                                <CheckCircle
                                                    size={22}
                                                    weight="fill"
                                                    className={
                                                        c.status === "confirmed"
                                                            ? "text-primary"
                                                            : "text-muted-foreground"
                                                    }
                                                />
                                            </button>
                                            <button
                                                onClick={() => respondForKid(p.id, "declined")}
                                                className="p-2 rounded-lg hover:bg-muted press"
                                                aria-label="Rifiuta"
                                            >
                                                <XCircle
                                                    size={22}
                                                    weight="fill"
                                                    className={
                                                        c.status === "declined"
                                                            ? "text-destructive"
                                                            : "text-muted-foreground"
                                                    }
                                                />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

            {/* Roster convocati (coach/admin) */}
            {(user?.role === "admin" || user?.role === "coach") && (
                <div className="rounded-2xl hairline bg-card p-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Convocati ({convs.length})
                    </div>
                    <div className="mt-3 grid gap-2">
                        {convs.map((c) => {
                            const p = players.find((x) => x.id === c.player_id);
                            return (
                                <div
                                    key={c.id}
                                    className="flex items-center gap-3 rounded-xl hairline p-3"
                                >
                                    <div className="w-9 h-9 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-bold">
                                        {p ? `${p.first_name[0]}${p.last_name[0]}` : "?"}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold truncate">
                                            {p
                                                ? `${p.first_name} ${p.last_name}`
                                                : "Sconosciuto"}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {p?.position || "-"}
                                        </div>
                                    </div>
                                    <StatusPill status={c.status} />
                                </div>
                            );
                        })}
                        {convs.length === 0 && (
                            <div className="text-sm text-muted-foreground">
                                Nessun convocato ancora impostato.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Materials */}
            {match.materials?.length > 0 && (
                <div className="rounded-2xl hairline bg-card p-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Materiale da portare
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {match.materials.map((m) => (
                            <label
                                key={m}
                                data-testid="materials-checklist-item"
                                className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-muted/60 press cursor-pointer"
                            >
                                <Checkbox
                                    checked={!!checked[m]}
                                    onCheckedChange={(v) =>
                                        setChecked((c) => ({ ...c, [m]: !!v }))
                                    }
                                />
                                <span
                                    className={`text-sm ${
                                        checked[m]
                                            ? "line-through text-muted-foreground"
                                            : ""
                                    }`}
                                >
                                    {m}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    if (status === "confirmed")
        return (
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-primary/15 text-primary flex items-center gap-1 w-fit">
                <CheckCircle size={12} weight="fill" /> Confermato
            </span>
        );
    if (status === "declined")
        return (
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-destructive/15 text-destructive flex items-center gap-1 w-fit">
                <XCircle size={12} weight="fill" /> Assente
            </span>
        );
    return (
        <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-muted text-muted-foreground flex items-center gap-1 w-fit">
            <HourglassMedium size={12} weight="fill" /> In attesa
        </span>
    );
}
